import React from 'react';
import {
  Box,
  Button,
  SxProps,
  Typography,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  Divider,
} from '@mui/material';
import {
  formatDateAsDayMonth,
  getDifficultyLevelString,
  IconWrapper,
  ImageWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import {
  MarkingSchemeEnum,
  MultiSubjectSection,
} from '@protos/learning_management/lms.db_pb';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import { getSubjectNameFromEnum } from '../../utils_assessment/assessmentUtils';
import { height, maxWidth, width } from '@mui/system';
import { StudentAssessmentTask } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { Timestamp } from '@bufbuild/protobuf';

// --- STYLES ---
const styles = {
  root: {
    display: 'flex',
    // flexDirection: { xs: 'column', md: 'row' },
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    borderRadius: { xs: pxToRem(12), md: pxTovW(12) },
    padding: { xs: pxToRem(12), md: pxTovW(12) },
    gap: { xs: pxToRem(15), md: pxTovW(20) },
    boxSizing: 'border-box',
    boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
    // cursor: 'pointer',
    width: '100%',
    maxWidth: { xs: '100%', lg: pxTovW(831) },
  },
  imageBox: {
    width: { xs: pxToRem(70), md: pxTovW(126) },
    height: { xs: pxToRem(90), md: pxTovW(139) },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0, // ✅ prevents shrinking
    // backgroundColor: '#FFF7D5',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexGrow: 1,
    minWidth: 0, // ✅ CRITICAL FIX

    paddingY: { xs: 0, md: pxTovW(4) },
  },
  headerBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(4), md: pxTovW(4) },
  },
  metaBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(4), md: pxTovW(4) },
    marginTop: { xs: pxToRem(4), md: pxTovW(4) },
  },
  additionalTopicsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(4), md: pxTovW(4) },
  },
  footerBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    justifyContent: 'space-between',
    marginTop: { xs: pxToRem(15), md: 0 },
  },
  statsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(20) },
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
  },
  startButton: {
    border: '2px solid #007CDC',
    color: '#007CDC',
    fontWeight: 700,
    textTransform: 'none',
    minWidth: 'auto',
    // width: { md: pxTovW(220) },
    width: { xs: pxToRem(132), md: pxTovW(250), lg: pxTovW(150) },
    height: { xs: pxToRem(40), md: pxTovW(70), lg: pxTovW(40) },
    padding: {
      // xs: `${pxToRem(4)} ${pxToRem(16)}`,
      // md: `${pxTovW(4)} ${pxTovW(16)}`,
    },
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    '&:hover': {
      backgroundColor: '#007CDC',
      color: '#FFFFFF',
      border: '2px solid #007CDC',
    },
  },
};

interface IProps {
  status: 'start' | 'resume' | 'complete';
  section: MultiSubjectSection;
  onStartTest?: () => void;
  rootStyle?: IStyles;
  subjectIcon: string;
  subjectColor: string;
  chapterListPopupHandler?: (section: MultiSubjectSection) => void;
  attempted?: number;
  ctaDisable?: boolean;
  taskCompletedDate?: Timestamp;
}
export const SubjectAssessmentCard = (props: IProps) => {
  const {
    status,
    section,
    onStartTest,
    rootStyle,
    subjectColor,
    subjectIcon,
    chapterListPopupHandler,
    attempted,
    ctaDisable,
    taskCompletedDate,
  } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;

  // const subjectName = section.subject;
  // const difficultyText = section.avgDifficultyLevel;
  // getDifficultyLevelFromString(section.avgDifficultyLevel) || 'Medium';
  // const chapterName =
  //   section.chapterInfo?.chapterName ||
  //   section.sectionTitle ||
  //   'Untitled Module';
  const additionalTopicsCount =
    section.chapterInfo?.chapterTopicsInfo.length || 0;
  // const subCategory = section.chapterInfo?.subCategory || '';
  const { label, color, backgroundColor } = getButtonConfig(status);

  return (
    <Box
      sx={{
        ...styles.root,
        ...rootStyle,
        cursor: ctaDisable ? 'default' : 'pointer',
      }}
      // onClick={() => onStartTest?.(section)}
    >
      <Box sx={{ ...styles.imageBox, backgroundColor: subjectColor }}>
        <ImageWrapper
          name={'subjects/english'}
          path={subjectIcon}
          type="png"
          parentFolder="icons"
          styles={{
            width: { xs: pxToRem(45), md: pxTovW(63) },
            height: { xs: pxToRem(45), md: pxTovW(63) },
          }}
        />
      </Box>

      <Box sx={styles.contentBox}>
        <Box sx={styles.headerBox}>
          <Typography
            variant="caption"
            sx={{ color: '#007CDC', fontWeight: 700, fontSize: pxToRem(12) }}
          >
            {getSubjectNameFromEnum(section.subject)}
          </Typography>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: pxToRem(16), md: pxTovW(18) },
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              maxWidth: '100%',
            }}
          >
            {section.sectionTitle}
          </Typography>

          <Box sx={styles.metaBox}>
            {additionalTopicsCount > 0 && (
              <Box
                sx={styles.additionalTopicsBox}
                onClick={(ev?: React.MouseEvent) => {
                  ev?.stopPropagation();
                  chapterListPopupHandler && chapterListPopupHandler(section);
                }}
              >
                <Typography sx={{ fontSize: pxToRem(11), fontWeight: 700 }}>
                  +{additionalTopicsCount} More
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: { xs: pxToRem(12), md: pxTovW(15) },
                    height: { xs: pxToRem(12), md: pxTovW(15) },
                    background: 'black',
                    borderRadius: {
                      xs: pxToRem(10),
                      md: pxTovW(20),
                      lg: pxTovW(15),
                    },
                  }}
                >
                  <Typography variant="smallestText" color={'common.white'}>
                    i
                  </Typography>
                </Box>
                {section.markingSchemeType ===
                MarkingSchemeEnum.MARKING_SCHEME_JEE ? (
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Typography variant="smallestText" color="primary.main">
                      JEE marking scheme
                    </Typography>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            )}
            {section.sectionName && (
              <Typography
                sx={{ fontSize: pxToRem(11), color: 'text.secondary' }}
              >
                {section.sectionName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={styles.footerBox}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {(status == 'resume' || status == 'complete') && attempted ? (
              <Typography>
                {attempted}Qs attempted out of {section.numberOfQuestion}
              </Typography>
            ) : (
              <Box sx={styles.statsBox}>
                <Box sx={styles.statItem}>
                  <IconWrapper
                    name="questions"
                    size="small"
                    type="png"
                    parentFolder="icons"
                  />
                  <Typography sx={{ fontSize: pxToRem(12), fontWeight: 700 }}>
                    {section.numberOfQuestion || 0}Qs
                  </Typography>
                </Box>

                <Box sx={styles.statItem}>
                  <IconWrapper
                    name={
                      section?.avgDifficultyLevel === 1
                        ? 'difficulty-hard'
                        : section?.avgDifficultyLevel === 2
                        ? 'level'
                        : 'difficulty-easy'
                    }
                    size={isIpadOnly ? 'md' : 'small'}
                    parentFolder="icons"
                    type="png"
                  />
                  <Typography
                    variant="smallestText"
                    sx={{ fontSize: pxToRem(12), fontWeight: 700 }}
                  >
                    {getDifficultyLevelString(section.avgDifficultyLevel)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            {ctaDisable ? (
              <Box
                sx={{
                  background: '#DAFFEB 0% 0% no-repeat padding-box',
                  border: '2px solid #0AA34F',
                  borderRadius: '10px',
                  padding: { xs: pxToRem(8), md: pxTovW(10) },
                  textAlign: 'center',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: 'black', fontWeight: 700 }}
                >
                  {`Attempt Completed on ${formatDateAsDayMonth(
                    taskCompletedDate
                  )}`}
                </Typography>
              </Box>
            ) : (
              <Button
                variant="outlined"
                sx={{
                  ...styles.startButton,
                  border: `2px solid ${color}`,
                  color: color,
                  backgroundColor: backgroundColor,
                  '&:hover': {
                    backgroundColor: color,
                    color: '#FFFFFF',
                    border: `2px solid ${color}`,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartTest?.();
                }}
              >
                <Typography
                  variant="smallText"
                  color="inherit"
                  fontWeight={700}
                >
                  {label}
                </Typography>
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const getButtonConfig = (status: 'start' | 'resume' | 'complete') => {
  switch (status) {
    case 'resume':
      return {
        label: 'RESUME TEST',
        color: '#E53935',
        backgroundColor: '#FFECEC',
      };
    case 'complete':
      return {
        label: 'COMPLETED',
        color: '#2E7D32',
        backgroundColor: '#CAFEE1',
      };
    default:
      return {
        label: 'START TEST',
        color: '#007CDC',
        backgroundColor: '#EBF6FF',
      };
  }
};
